<template>
  <div class="chat-window">
    <div
      v-if="!expanded"
      class="chat-icon"
      @click="() => toogle_chat()"
      :class="{ open: expanded }"
    >
      <div class="icon-wrapper">
        <CommunityIcon></CommunityIcon>
      </div>
    </div>
    <a-layout v-else class="container">
      <a-layout-header class="header">
        <a-row justify="space-between" class="header-container">
          <a-col flex="auto" class="header-col">
            <span
              ><b>{{ assistant_store.name }}</b></span
            >
          </a-col>
          <a-col flex="55px" class="header-col">
            <a-button
              :icon="h(CloseOutlined)"
              type="text"
              shape="circle"
              @click="() => toogle_chat()"
            ></a-button>
          </a-col>
        </a-row>
      </a-layout-header>
      <a-layout-content class="main-content">
        <error-display v-if="!assistant_store.client_key"> Client Key not provided! </error-display>
        <loading-display
          v-else-if="!assistant_store.error_message && !assistant_store.is_initialized"
        >
          Initializing...
        </loading-display>
        <loading-display v-else-if="assistant_store.is_connecting">Connecting...</loading-display>
        <error-display v-else-if="!assistant_store.is_connected">
          <a-space direction="vertical">
            <a-row justify="center"
              ><img height="32px" src="@/assets/img/server-error.png"
            /></a-row>
            <a-row justify="center"><pre>Assistant Disconnected!</pre></a-row>
            <a-row justify="center">
              <pre class="error-messsage">Reason: {{ assistant_store.error_message }}</pre>
            </a-row>
            <a-row justify="center">
              <a-button @click="() => assistant_store.reconnect()">Reconnect</a-button>
            </a-row>
          </a-space>
        </error-display>
        <a-list
          v-else
          ref="message_list"
          item-layout="horizontal"
          :data-source="assistant_store.messages"
          class="main-content"
        >
          <template #renderItem="{ item }:RenderListItem">
            <a-list-item class="message-list-item">
              <a-comment class="message-content">
                <template #actions> </template>
                <template #author>
                  {{ ActorToAuthor[item.value.actor] }}
                </template>
                <template #avatar>
                  <component :is="item.value.avatar" />
                </template>
                <template #content>
                  <div :class="{ cancelled: item.value.is_cancelled }">
                    <div v-html="marked(item.value.content)"></div>
                  </div>
                  <component v-if="item.value.is_loading" :is="spinner"></component>
                </template>
                <template #datetime>
                  <a-tooltip :title="dayjs(item.value.created_at).format('YYYY-MM-DD HH:mm:ss')">
                    <span>{{ dayjs(item.value.created_at).fromNow() }}</span>
                  </a-tooltip>
                </template>
              </a-comment>
            </a-list-item>
          </template>
        </a-list>
      </a-layout-content>
      <a-layout-footer class="footer">
        <a-input-group compact v-if="assistant_store.is_initialized">
          <a-button type="default" :icon="h(AudioOutlined)"></a-button>
          <a-input
            :disabled="assistant_store.is_busy"
            v-model:value="chat_input"
            type="textarea"
            placeholder="How can I help?"
            style="width: calc(100% - 64px)"
            @keyup.enter="send_message"
          />
          <a-button
            v-if="assistant_store.is_busy"
            danger
            :icon="h(StopIcon)"
            @click="() => assistant_store.cancel_last_message()"
          ></a-button>
          <a-button v-else type="primary" :icon="h(SendOutlined)" @click="send_message"></a-button>
        </a-input-group>
      </a-layout-footer>
    </a-layout>
  </div>
</template>
<script setup lang="tsx">
import { h, nextTick, ref, type Ref } from 'vue'
import {
  AudioOutlined,
  SendOutlined,
  CloseOutlined,
  MinusCircleFilled as StopIcon,
} from '@ant-design/icons-vue'
import CommunityIcon from '../icons/IconCommunity.vue'
import { useAssistantStore, ActorToAuthor, type Message } from '@/stores/assistant.tsx'
import spinner from '@/components/core/spinner.vue'
import ErrorDisplay from '@/components/error/error-display.vue'
import LoadingDisplay from '@/components/core/loading-display.vue'
import { marked } from 'marked'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

type RenderListItem = { item: Ref<Message> }
const assistant_store = useAssistantStore()

const max_width = 400
const max_height = 500

const message_list = ref()
const expanded = ref(true)
const width = ref(62)
const height = ref(62)
const right = ref(10)
const bottom = ref(0)
const radius = ref(10)
const header_height = 50
const footer_height = 60
const chat_input = ref('What is my water bill for March & April 2024, customer id is 1234')

function toogle_chat() {
  expanded.value = !expanded.value
  validate()
}
function validate() {
  if (expanded.value) {
    width.value = max_width
    height.value = max_height - header_height - footer_height
    right.value = 10
    bottom.value = 0
    radius.value = 10
  } else {
    width.value = 62
    height.value = 62
    right.value = 41
    bottom.value = 59
    radius.value = 35
  }
}
validate()

async function send_message() {
  if (assistant_store.is_busy) {
    return
  }
  const user_input = chat_input.value
  if (!user_input) {
    console.error('Please enter a valid message!')
    return
  }
  chat_input.value = ''
  const promise = assistant_store.send_message(user_input)
  nextTick(scroll_chat)
  await promise
  nextTick(scroll_chat)
}

assistant_store.$subscribe(() => {
  nextTick(scroll_chat)
})

function scroll_chat() {
  if (message_list.value?.$el) {
    const el = message_list.value.$el
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }
}
</script>
<style scoped>
.chat-window {
  color: var(--color-text);
  width: calc(v-bind(width) * 1px);
  position: fixed;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.2);

  border-radius: calc(v-bind(radius) * 1px);
  right: calc(v-bind(right) * 1px);
  bottom: calc(v-bind(bottom) * 1px);
}
.container {
  background-color: var(--color-background);
  border-radius: 10px 10px 0 0;
  border: 1px solid var(--color-border);
}
.header {
  padding-left: 15px;
  height: calc(v-bind(header_height) * 1px);
  line-height: calc(v-bind(header_height) * 1px);
  background-color: unset;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
}
.header-container {
  width: calc(v-bind(width) * 1px);
}
.chat-icon {
  cursor: pointer;
  display: flex;
  position: relative;
  background-color: var(--color-background);
  border: 2px solid var(--color-border);
  height: 72px;
  width: 72px;
  border-radius: 60px;
  /* right: calc(95px - v-bind(width) * 1px); */
  /* bottom: 61px; */
}
.open {
  background-color: unset;
}
.icon-wrapper {
  position: relative;
  display: flex;
  left: 24px;
  top: 24px;
}
.main-content {
  height: calc(v-bind(height) * 1px);
  overflow: scroll;
}
.message-content::v-deep(.ant-comment-inner) {
  padding: 0;
}
.message-list-item {
  padding: 20px;
  padding-bottom: 0px;
  border: unset;
}
.error-messsage {
  text-wrap: auto;
}
.cancelled {
  text-decoration: line-through;
}
.footer {
  color: var(--color-text);
  height: calc(v-bind(footer_height) * 1px);
  padding: 10px 15px;
  background-color: unset;
}
</style>
